import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Download, AlertTriangle, CheckCircle2, ShieldAlert, ArrowRight, ClipboardCheck,
} from "lucide-react";
import { Helmet } from "react-helmet-async";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const RATING_BAND = {
  GREEN: { bg: "bg-[#064E2E]", label: "GOOD CONDITION", note: "Maintain as normal." },
  AMBER: { bg: "bg-[#B88A2A]", label: "MONITOR",       note: "Some items need attention soon." },
  RED:   { bg: "bg-[#B91C1C]", label: "ACTION REQUIRED", note: "We recommend acting before the next inspection." },
};
const RATING_DOT = {
  GREEN: "bg-[#064E2E]",
  AMBER: "bg-[#B88A2A]",
  RED:   "bg-[#B91C1C]",
};

export default function HealthCheckPublicViewer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/health-checks/${token}`);
        if (active) setData(data);
      } catch {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="section-pad container-phs flex items-center gap-2 text-[#475569]" data-testid="hc-public-loading">
        <Loader2 className="animate-spin" size={18}/> Loading your Health Check report…
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="section-pad container-phs max-w-xl text-center" data-testid="hc-public-notfound">
        <AlertTriangle className="mx-auto text-[#B88A2A]" size={36}/>
        <h1 className="font-display text-3xl mt-4 text-[#071A33]">Report not found</h1>
        <p className="mt-2 text-[#475569]">This link is invalid or the report has been withdrawn. Please use the latest link in your email.</p>
        <Link to="/contact" className="btn-primary mt-6 inline-flex">Contact us <ArrowRight size={16}/></Link>
      </div>
    );
  }

  const band = RATING_BAND[data.overall_rating] || { bg: "bg-[#475569]", label: "PENDING", note: "Awaiting final review." };
  const pdfUrl = `${BACKEND}/api/health-checks/${token}/pdf`;

  return (
    <div className="bg-[#F7F4ED]" data-testid="hc-public-viewer">
      <Helmet>
        <title>Annual Health Check Report · Park Home Support</title>
        <meta name="robots" content="noindex,nofollow"/>
      </Helmet>

      <div className="container-phs py-10 lg:py-14">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <p className="eyebrow">Annual External Park Home Health Check</p>
            <h1 className="font-display text-2xl sm:text-3xl text-[#071A33] mt-0.5">{data.member_name || "Your Health Check report"}</h1>
            <p className="text-sm text-[#475569] mt-1">{[data.park_name, data.property_address].filter(Boolean).join(" · ") || "—"}</p>
          </div>
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-primary h-10 py-0 px-4 text-sm" data-testid="hc-public-pdf-btn">
            <Download size={14}/> Download PDF
          </a>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4 space-y-4">
            <div className={`${band.bg} text-white rounded-2xl p-6 border-t-4 border-[#B88A2A]`} data-testid="hc-public-rating-band">
              <p className="text-xs tracking-[0.22em] uppercase font-medium opacity-80">Overall Rating</p>
              <p className="font-display text-3xl mt-1">{data.overall_rating || "PENDING"}</p>
              <p className="text-white/90 mt-1">{band.label}</p>
              <p className="text-white/85 text-sm mt-3 leading-relaxed">{band.note}</p>
            </div>

            <div className="card-default" data-testid="hc-public-meta">
              <p className="eyebrow">Inspection</p>
              <div className="mt-3 space-y-2 text-sm">
                <Meta k="Inspector"     v={data.inspector_name}/>
                <Meta k="Inspection date" v={formatDate(data.inspection_date)}/>
                <Meta k="Completed"     v={formatDate(data.completed_at)}/>
                <Meta k="Next due"      v={formatDate(data.next_due_at)}/>
              </div>
            </div>

            <div className="card-dark">
              <p className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs font-medium">Member benefit</p>
              <p className="font-display text-xl mt-2">Your annual peace-of-mind</p>
              <p className="text-sm text-white/80 mt-2">This Health Check is included with your Park Home Support membership. We&apos;ll automatically remind you when next year&apos;s is due.</p>
              <Link to="/membership" className="btn-accent mt-4 w-full" data-testid="hc-public-membership-cta">Membership benefits <ArrowRight size={14}/></Link>
            </div>
          </aside>

          <main className="lg:col-span-8 space-y-5">
            {data.executive_summary && (
              <section className="card-default" data-testid="hc-public-exec">
                <p className="eyebrow">Executive Summary</p>
                <h2 className="font-display text-xl text-[#071A33] mt-1">Overall condition</h2>
                <p className="mt-3 text-[#071A33] leading-relaxed whitespace-pre-wrap">{data.executive_summary}</p>
              </section>
            )}

            {(data.recommendations || []).length > 0 && (
              <section className="card-default" data-testid="hc-public-recs">
                <p className="eyebrow">Recommendations</p>
                <h2 className="font-display text-xl text-[#071A33] mt-1">What to action and when</h2>
                <ul className="mt-4 space-y-3">
                  {data.recommendations.map((r) => (
                    <li key={r.id} className="border-l-4 border-[#B88A2A]/40 pl-4 py-1">
                      <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#475569]">
                        <span className={
                          r.priority === "HIGH" ? "text-[#B91C1C]" :
                          r.priority === "LOW" ? "text-[#0F766E]" : "text-[#B88A2A]"
                        }>{r.priority || "MED"}</span>
                        <span className="text-[#94A3B8] mx-1.5">·</span>
                        <span>{(r.timescale || "").replace("_", " ").toLowerCase()}</span>
                      </p>
                      <p className="text-[#071A33] mt-0.5">{r.text}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(data.sections || []).map((s) => (
              <section key={s.key} className="card-default" data-testid={`hc-public-section-${s.key}`}>
                <h3 className="font-display text-lg text-[#071A33]">{s.title}</h3>
                {s.section_notes && (
                  <p className="mt-2 text-[#334155] text-sm whitespace-pre-wrap">{s.section_notes}</p>
                )}
                <ul className="mt-4 space-y-2">
                  {(s.items || []).filter((it) => it.rating).map((it) => (
                    <li key={it.key} className="flex items-start gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${RATING_DOT[it.rating] || "bg-[#94A3B8]"}`}/>
                      <div className="flex-1">
                        <p className="text-sm text-[#071A33] font-medium">{it.label} <span className="text-[10px] uppercase tracking-[0.18em] text-[#475569]">· {it.rating}</span></p>
                        {it.notes && <p className="text-[13px] text-[#334155] mt-1 leading-snug">{it.notes}</p>}
                      </div>
                    </li>
                  ))}
                  {(s.items || []).filter((it) => it.rating).length === 0 && (
                    <li className="text-xs text-[#475569] italic">No items rated in this section.</li>
                  )}
                </ul>
              </section>
            ))}

            <div className="card-default border-l-4 border-[#B88A2A] bg-[#FFF8EC]" data-testid="hc-public-disclaimer">
              <div className="flex gap-3 items-start">
                <ShieldAlert className="text-[#B88A2A] mt-0.5 shrink-0" size={20}/>
                <p className="text-sm text-[#3F2E10] leading-relaxed">
                  <strong>How to use this report.</strong> This Annual Health Check is a non-invasive external visual assessment by Park Home Support.
                  It is not a structural survey, insurance product or warranty. We do not access roof voids, behind cladding or beneath the home. Final decisions on whether to proceed with any work, claim or insurance application remain with you.
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function Meta({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex justify-between gap-3 border-b border-[#E2DCD0] pb-2 last:border-0 last:pb-0">
      <span className="text-[#475569]">{k}</span>
      <span className="text-[#071A33] font-medium text-right">{v}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
}
